import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { ModuleTableComponent } from '../module-table/module-table.component';
import { InfoscreenListColumn, InfoscreenListMode, InfoscreenPeriod } from '../infoscreen-module.types';
import { PagePieComponent } from '../../school-calendar/page-pie.component';
import { listScrollFraction } from '../scroll-speed';

/** Length of the fade between two pages. */
const FADE_MS = 500;

/**
 * The day's bookings as a list — the old screen's table. When it is taller than the space it
 * either scrolls vertically in a seamless loop, at the screen's own speed, or (`mode` = `Pages`)
 * shows a page at a time and fades to the next every `pageSeconds`.
 *
 * The full table is always laid out (hidden while paging): it is what the scroll moves and what
 * the pages are measured from, so rows are never cut in half.
 */
@Component({
  selector: 'app-list-view',
  imports: [ModuleTableComponent, PagePieComponent],
  template: `
    <div #viewport class="viewport">
      <div
        class="track"
        [class.scrolling]="scrolling()"
        [class.measuring]="mode() === 'Pages'"
        [attr.aria-hidden]="mode() === 'Pages' ? 'true' : null"
        [style.--scroll-duration.s]="durationSeconds()"
      >
        <div #first>
          <app-module-table [dataSource]="upcoming()" [displayDate]="now()" [columns]="columns()" />
        </div>
        @if (scrolling()) {
          <app-module-table [dataSource]="upcoming()" [displayDate]="now()" [columns]="columns()" />
        }
      </div>

      @if (mode() === 'Pages') {
        <div class="page" [class.fading]="fading()">
          <app-module-table [dataSource]="pageRows()" [displayDate]="now()" [columns]="columns()" />
        </div>
        @if (pageCount() > 1) {
          <app-page-pie class="pager" [index]="pageIndex()" [count]="pageCount()" [seconds]="pageSecondsClamped()" />
        }
      }

      @if (!upcoming().length) {
        <div class="empty">Ingen flere aktiviteter i dag</div>
      }
    </div>
  `,
  styles: `
    @keyframes seamless-scroll {
      from { transform: translateY(0); }
      to { transform: translateY(-50%); }
    }
    :host, .viewport { display: block; height: 100%; overflow: hidden; position: relative; }
    .track.scrolling {
      animation: seamless-scroll var(--scroll-duration, 20s) linear infinite;
      will-change: transform;
    }
    /* A 1 px line moving by fractions of a pixel lands on one row of pixels, then two at half
       strength, and flickers. 2 px always covers at least one full row. Only while scrolling (the
       old screen does the same in infoscreen.component.scss). */
    .track.scrolling ::ng-deep mat-row {
      border-bottom-width: 2px;
    }
    /* Laid out at full width for measuring, never seen. */
    .track.measuring {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      visibility: hidden;
      pointer-events: none;
    }
    .page {
      transition: opacity ${FADE_MS}ms ease-in-out;
      &.fading { opacity: 0; }
    }
    /* Bottom right, in space the pages leave free for it (see measure), so it never covers a row. */
    .pager {
      position: absolute;
      bottom: calc(var(--cell-pad-tb) / 3);
      right: var(--gutter-size);
      padding: 0.3em 0.7em;
      border-radius: 1em;
      background: rgba(var(--ink-rgb, 255, 255, 255), 0.12);
    }
    .empty {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: var(--fontsize-medium);
      opacity: 0.8;
    }
  `,
})
export class ListViewComponent implements AfterViewInit, OnDestroy {
  readonly periods = input.required<InfoscreenPeriod[]>();
  readonly now = input.required<Date>();
  /** The screen's columns. */
  readonly columns = input.required<InfoscreenListColumn[]>();
  /** 1 (slow) – 10 (fast). Scroll mode only. */
  readonly speed = input(5);
  /** Scroll, or a page at a time. */
  readonly mode = input<InfoscreenListMode>('Scroll');
  /** Seconds per page. Pages mode only. */
  readonly pageSeconds = input(20);

  private readonly viewport = viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly first = viewChild.required<ElementRef<HTMLElement>>('first');

  /** True while the list scrolls vertically — the screen pages the ad band meanwhile. */
  readonly scrollingChange = output<boolean>();

  /** True when the whole list is taller than the space. */
  private readonly overflows = signal(false);
  readonly scrolling = computed(() => this.mode() === 'Scroll' && this.overflows());
  readonly durationSeconds = signal(20);

  /** Row indices per page, in Pages mode. */
  private readonly pages = signal<number[][]>([]);
  readonly pageIndex = signal(0);
  readonly fading = signal(false);

  readonly pageSecondsClamped = computed(() => Math.max(5, this.pageSeconds()));

  /** A number, not the pages: a re-measure that finds the same pages must not restart the cycle. */
  readonly pageCount = computed(() => this.pages().length);

  /** Bookings that have not ended yet, as on the old screen. */
  readonly upcoming = computed(() => {
    const now = this.now().getTime();
    return this.periods().filter((p) => new Date(p.end).getTime() > now);
  });

  readonly pageRows = computed(() => {
    const rows = this.upcoming();
    const page = this.pages()[this.pageIndex()];
    return page ? page.map((i) => rows[i]).filter(Boolean) : rows;
  });

  private resizeObserver?: ResizeObserver;
  private pageTimer?: ReturnType<typeof setInterval>;
  private fadeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    // A new list, speed or mode: measure again once the table has rendered.
    effect(() => {
      this.upcoming();
      this.speed();
      this.mode();
      queueMicrotask(() => setTimeout(() => this.measure()));
    });

    effect(() => this.scrollingChange.emit(this.scrolling()));

    // Pages mode: fade out, show the next page, fade in.
    effect(() => {
      const paging = this.mode() === 'Pages';
      const count = this.pageCount();
      const seconds = this.pageSecondsClamped();

      this.stopPaging();
      if (untracked(this.pageIndex) >= count) this.pageIndex.set(0);
      if (!paging || count <= 1) return;

      this.pageTimer = setInterval(() => {
        this.fading.set(true);
        this.fadeTimer = setTimeout(() => {
          this.pageIndex.update((i) => (i + 1) % count);
          this.fading.set(false);
        }, FADE_MS);
      }, seconds * 1000);
    });
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.viewport().nativeElement);
    this.resizeObserver.observe(this.first().nativeElement);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.stopPaging();
  }

  private stopPaging() {
    clearInterval(this.pageTimer);
    clearTimeout(this.fadeTimer);
    this.fading.set(false);
  }

  private measure() {
    const visible = this.viewport().nativeElement.clientHeight;
    const content = this.first().nativeElement.offsetHeight;
    const overflows = !!visible && content > visible;

    if (this.mode() === 'Pages') {
      // Leave a strip at the bottom for the "1/3" pager.
      const fontSize = parseFloat(getComputedStyle(this.viewport().nativeElement).getPropertyValue('--fontsize-small')) || 22;
      const pagerReserve = fontSize * 2.4;
      this.overflows.set(overflows);
      this.pages.set(overflows ? pagesThatFit(this.first().nativeElement, visible - pagerReserve) : []);
      return;
    }

    if (!overflows) {
      this.overflows.set(false);
      return;
    }

    this.durationSeconds.set(Math.max(10, content / (visible * listScrollFraction(this.speed()))));
    this.overflows.set(true);
  }
}

/**
 * Groups the measured table rows into pages that each fit the height, in order. A row taller than
 * the space gets a page of its own.
 */
function pagesThatFit(table: HTMLElement, available: number): number[][] {
  const rows = Array.from(table.querySelectorAll('mat-row')) as HTMLElement[];

  const pages: number[][] = [];
  let current: number[] = [];
  let used = 0;

  rows.forEach((row, index) => {
    const height = row.offsetHeight;
    if (current.length && used + height > available) {
      pages.push(current);
      current = [];
      used = 0;
    }
    current.push(index);
    used += height;
  });
  if (current.length) pages.push(current);

  return pages;
}
